import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { RECALL_SUBJECTS } from '../data/recallCurriculum';

const MARKERS = [
  { size: '7px', color: 'hsl(var(--primary))' },
  { size: '5px', color: 'hsl(0 0% 62%)' },
  { size: '4px', color: 'hsl(0 0% 75%)' },
];

const emptyLine = () => ({ text: '', indent: 0 });

export default function RecallPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [totals, setTotals] = useState({ sessions: 0, minutes: 0, bullets: 0 });
  const [expanded, setExpanded] = useState({ 'Chemistry-0': true });

  const [session, setSession] = useState(null); // { subject, level, topic, code, name }
  const [lines, setLines] = useState([emptyLine()]);
  const [secs, setSecs] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [copied, setCopied] = useState(false);

  const lineRefs = useRef([]);
  const pendingFocus = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/recall/stats');
        if (!active) return;
        setMeta(res.data.meta || {});
        setTotals(res.data.totals || { sessions: 0, minutes: 0, bullets: 0 });
      } catch {
        if (active) toast.error('Failed to load recall stats');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [api]);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const finish = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setShowSummary(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && session && !showSummary) finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, showSummary, finish]);

  useLayoutEffect(() => {
    if (pendingFocus.current) {
      const { idx, pos } = pendingFocus.current;
      pendingFocus.current = null;
      focusLine(idx, pos);
    }
  }, [lines]);

  useEffect(() => {
    if (session && lineRefs.current[0]) lineRefs.current[0].focus();
  }, [session]);

  const focusLine = (i, pos) => {
    const el = lineRefs.current[i];
    if (!el) return;
    el.focus();
    const p = pos < 0 ? el.value.length : pos;
    try { el.setSelectionRange(p, p); } catch {}
  };

  const startSession = (subject, level, topic, sub) => {
    lineRefs.current = [];
    setSession({ subject, level, topic, code: sub.code, name: sub.name });
    setShowSummary(false);
    setCopied(false);
    setSecs(0);
    setLines([emptyLine()]);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
  };

  const handleKeyDown = (e, i) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); finish(); return; }

    if (e.key === 'Enter') {
      e.preventDefault();
      pendingFocus.current = { idx: i + 1, pos: 0 };
      setLines((prev) => {
        const L = prev.slice();
        L.splice(i + 1, 0, { text: '', indent: prev[i].indent });
        return L;
      });
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      setLines((prev) => {
        const L = prev.slice();
        L[i] = { ...L[i], indent: e.shiftKey ? Math.max(0, L[i].indent - 1) : Math.min(2, L[i].indent + 1) };
        return L;
      });
      return;
    }

    const el = e.target;
    if (e.key === 'Backspace' && el.selectionStart === 0 && el.selectionEnd === 0 && i > 0) {
      e.preventDefault();
      setLines((prev) => {
        const prevLen = prev[i - 1].text.length;
        pendingFocus.current = { idx: i - 1, pos: prevLen };
        const L = prev.slice();
        L[i - 1] = { ...L[i - 1], text: L[i - 1].text + L[i].text };
        L.splice(i, 1);
        return L;
      });
      return;
    }

    if (e.key === 'ArrowUp' && i > 0 && el.selectionStart === 0) {
      e.preventDefault();
      focusLine(i - 1, -1);
    }
    if (e.key === 'ArrowDown' && i < lines.length - 1 && el.selectionEnd === el.value.length) {
      e.preventDefault();
      focusLine(i + 1, -1);
    }
  };

  const handleLineChange = (e, i) => {
    const v = e.target.value;
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
    setLines((prev) => {
      const L = prev.slice();
      L[i] = { ...L[i], text: v };
      return L;
    });
  };

  const setLineRef = (el, i) => {
    lineRefs.current[i] = el;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  };

  const handlePadClick = () => focusLine(lines.length - 1, -1);

  const copyPoints = () => {
    const text = lines
      .filter((l) => l.text.trim())
      .map((l) => '  '.repeat(l.indent) + '- ' + l.text.trim())
      .join('\n');
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(fallback);
    } else {
      fallback();
    }
    setCopied(true);
  };

  const doneSession = async () => {
    if (!session) return;
    const pts = lines.filter((l) => l.text.trim()).length;
    const words = lines.reduce((a, l) => a + (l.text.trim() ? l.text.trim().split(/\s+/).length : 0), 0);
    const key = `${session.subject}|${session.code}`;
    const mins = Math.max(1, Math.round(secs / 60));
    try {
      const res = await api.post('/recall/sessions', {
        subject: session.subject,
        courseLevel: session.level,
        topic: session.topic,
        subtopicCode: session.code,
        subtopicName: session.name,
        durationSeconds: secs,
        pointsCount: pts,
        wordsCount: words,
      });
      const created = res.data.session;
      setMeta((prev) => ({ ...prev, [key]: { n: (prev[key]?.n || 0) + 1, lastAt: created.createdAt } }));
      setTotals((prev) => ({
        sessions: prev.sessions + 1,
        minutes: prev.minutes + mins,
        bullets: prev.bullets + pts,
      }));
    } catch {
      toast.error('Failed to save this session — your points were not recorded');
    } finally {
      setSession(null);
      setShowSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  const pts = lines.filter((l) => l.text.trim()).length;
  const words = lines.reduce((a, l) => a + (l.text.trim() ? l.text.trim().split(/\s+/).length : 0), 0);
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, '0');
  const statTime = (Math.round(totals.minutes / 6) / 10) + 'h';
  const statTopics = Object.keys(meta).length;

  return (
    <>
    <div className="max-w-[980px] mx-auto animate-fade-in">
      {/* Header */}
      <div className="border-b border-border pb-5 mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="label-swiss mb-1">Study</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Recall</h1>
        </div>
        <p className="text-[13px] text-muted-foreground text-right m-0">
          Notes on one screen, this on the other.<br />Nothing to file afterwards.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-border bg-card mb-6">
        <StatCell label="Sessions" value={totals.sessions} />
        <StatCell label="Time writing" value={statTime} className="border-l border-border" />
        <StatCell label="Points written" value={totals.bullets} className="border-l border-border" />
        <StatCell label="Subtopics touched" value={statTopics} className="border-l border-border" />
      </div>

      {/* Subjects */}
      {RECALL_SUBJECTS.map((subject) => {
        const allSubs = subject.topics.flatMap((tp) => tp.subs);
        const doneN = allSubs.filter((sb) => meta[`${subject.name}|${sb.code}`]).length;
        return (
          <section key={subject.name} className="bg-card border border-border mb-5">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
              <span className="text-[15px] font-bold tracking-tight">{subject.name}</span>
              <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 border border-border text-muted-foreground font-mono">
                {subject.level}
              </span>
              <span className="ml-auto text-[11px] text-muted-foreground font-mono">
                {doneN} / {allSubs.length} recalled
              </span>
            </div>
            {subject.topics.map((topic, ti) => {
              const topicKey = `${subject.name}-${ti}`;
              const open = !!expanded[topicKey];
              return (
                <div key={topicKey}>
                  <div
                    onClick={() => setExpanded((prev) => ({ ...prev, [topicKey]: !open }))}
                    className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer border-t border-[hsl(40_10%_93%)] hover:bg-[hsl(40_10%_96%)]"
                  >
                    <span className="w-3 text-[10px] text-muted-foreground font-mono">{open ? '▾' : '▸'}</span>
                    <span className="text-[13.5px] font-medium">{topic.name}</span>
                    <span className="ml-auto text-[10.5px] text-muted-foreground/70 font-mono">
                      {topic.subs.length} subtopics
                    </span>
                  </div>
                  {open && (
                    <div className="border-t border-[hsl(40_10%_93%)] animate-slide-up">
                      {topic.subs.map((sb) => {
                        const m = meta[`${subject.name}|${sb.code}`];
                        return (
                          <div
                            key={sb.code}
                            onClick={() => startSession(subject.name, subject.level, topic.name, sb)}
                            className="flex items-center gap-3 pt-2 pr-4 pb-2 pl-[38px] cursor-pointer hover:bg-primary/[0.04]"
                          >
                            <span className="w-10 shrink-0 text-[11px] text-muted-foreground font-mono">{sb.code}</span>
                            <span className="text-[13px]">{sb.name}</span>
                            <span className="ml-auto text-[10.5px] text-muted-foreground/70 font-mono whitespace-nowrap">
                              {m ? `${m.n}× · ${format(parseISO(m.lastAt), 'MMM d')}` : '—'}
                            </span>
                            <span className="text-xs text-primary/60">→</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
      <p className="mt-1 mb-8 text-[11.5px] text-muted-foreground/70">
        Click any subtopic to open a blank pad. Your last recall date and session count sit on the right — the app never schedules you.
      </p>
    </div>

    {/* Rendered outside the wrapper above: that div carries a forwards-filled
        fade-in animation, which creates a stacking context and would trap
        these fixed overlays below the sidebar regardless of z-index. */}
    {/* Session overlay */}
      {session && (
        <div className="fixed inset-0 bg-background z-[100] flex flex-col animate-fade-in">
          <div className="flex items-center gap-4 px-6 py-3 border-b border-border bg-card">
            <div className="w-3 h-3 bg-primary shrink-0" />
            <span className="label-swiss">
              {session.subject} {session.level} · {session.topic}
            </span>
            <div className="ml-auto flex items-center gap-4">
              <span className="text-[13px] font-semibold font-mono">{mm}:{ss}</span>
              <span className="text-[11px] text-muted-foreground font-mono">{pts} pts</span>
              <Button size="sm" onClick={finish}>Finish</Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex justify-center">
            <div className="w-[min(680px,88%)] pt-[52px]">
              <p className="mb-1.5 text-[11px] font-semibold text-primary font-mono">{session.code}</p>
              <h2 className="mb-1.5 text-[27px] font-bold tracking-tight">{session.name}</h2>
              <p className="mb-9 text-[13px] text-muted-foreground">
                Type as you read. Nothing is filed unless you copy it out.
              </p>
              {lines.map((ln, i) => {
                const mk = MARKERS[Math.min(ln.indent, 2)];
                return (
                  <div key={i} className="flex items-start gap-3.5 py-0.5" style={{ marginLeft: ln.indent * 26 }}>
                    <div className="w-3 shrink-0 pt-3">
                      <div style={{ width: mk.size, height: mk.size, background: mk.color }} />
                    </div>
                    <textarea
                      rows={1}
                      spellCheck={false}
                      placeholder={i === 0 && lines.length === 1 && !ln.text ? 'First point…' : ''}
                      value={ln.text}
                      onChange={(e) => handleLineChange(e, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      ref={(el) => setLineRef(el, i)}
                      className="w-full border-none outline-none bg-transparent resize-none overflow-hidden font-sans text-[19px] leading-[1.55] text-foreground py-0.5 block"
                    />
                  </div>
                );
              })}
              <div className="h-[220px] cursor-text" onClick={handlePadClick} />
            </div>
          </div>
          <div className="flex justify-center gap-6 px-6 py-3 border-t border-[hsl(40_10%_93%)]">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 font-mono">Enter — new point</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 font-mono">Tab — indent</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 font-mono">Esc — finish</span>
          </div>
        </div>
      )}

      {/* Summary modal */}
      {session && showSummary && (
        <div className="fixed inset-0 bg-foreground/35 z-[200] flex items-center justify-center animate-fade-in">
          <div className="bg-card border border-border w-[420px] max-w-[90vw] px-7 py-[26px] animate-slide-up">
            <p className="mb-1 label-swiss">Session complete</p>
            <h3 className="mb-[18px] text-lg font-bold tracking-tight">{session.name}</h3>
            <div className="grid grid-cols-3 border border-border mb-4">
              <div className="px-4 py-3.5">
                <p className="m-0 text-xl font-bold font-mono">{pts}</p>
                <p className="mt-0.5 text-[9.5px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Points</p>
              </div>
              <div className="px-4 py-3.5 border-l border-border">
                <p className="m-0 text-xl font-bold font-mono">{words}</p>
                <p className="mt-0.5 text-[9.5px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Words</p>
              </div>
              <div className="px-4 py-3.5 border-l border-border">
                <p className="m-0 text-xl font-bold font-mono">{Math.max(1, Math.round(secs / 60))}</p>
                <p className="mt-0.5 text-[9.5px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Minutes</p>
              </div>
            </div>
            <p className="mb-5 text-[12.5px] text-muted-foreground leading-relaxed">
              The session is logged against this subtopic. The points themselves are discarded — the writing was the point.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={copyPoints}>
                {copied ? 'Copied ✓' : 'Copy points'}
              </Button>
              <Button size="sm" onClick={doneSession}>Discard &amp; done</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatCell({ label, value, className = '' }) {
  return (
    <div className={`py-[18px] px-5 ${className}`}>
      <p className="m-0 text-2xl font-bold font-mono tracking-tight text-foreground">{value}</p>
      <p className="label-swiss mt-1">{label}</p>
    </div>
  );
}
